import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"
import controller from "../utils/controller"

export default class extends Controller {
  static targets = [
    "enable",
    "disable",
    "disabled",
    "enabled",
    "edit",
    "radius",
    "latitude",
    "longitude",
    "address",
    "radiusSelect",
    "addressInput",
    "coordinatesInput",
    "thumbnail",
    "map"
  ]

  static values = {
    searchUrl: String,
    latitude: Number,
    longitude: Number,
    address: String,
    radius: Number
  }

  connect() {
    this.loadState()
    this.updateMap(this.thumbnailTarget)
    this.toggle()
  }

  toggle() {
    this.enabledTarget.classList.toggle("hidden", !this.enabled)
    this.disabledTarget.classList.toggle("hidden", this.enabled)
    this.thumbnailTarget.dataset.mapDisabledValue = !this.enabled
  }

  edit() {
    if (!this.enabled) return

    this.reset()
    this.editModal.open()
  }

  selectRadius() {
    if (this.radiusSelectTarget.value !== "") {
      this.setRadius(this.radiusSelectTarget.value)
    }
  }

  async updateCenter() {
    const center = await this.getCenter()

    if (center) this.setCenter(center)
  }

  async getCenter() {
    if (this.coordinatesInputTarget.value !== "") {
      const [latitude, longitude] = this.coordinatesInputTarget.value.split(",")
      return { latitude, longitude, address: this.addressInputTarget.value }
    } else {
      const results = await this.search(this.addressInputTarget.value)
      return results[0]
    }
  }

  setCenter({ latitude, longitude, address }) {
    this.latitudeValue = latitude
    this.longitudeValue = longitude
    this.addressValue = address

    this.updateMap(this.mapTarget)
  }

  setRadius(value) {
    this.radiusValue = value

    this.updateMap(this.mapTarget)
  }

  customRadius(event) {
    this.radiusValue = event.detail.radius
    this.radiusSelect.clear()
  }

  apply() {
    this.applyChanges()
    this.editModal.close()
  }

  cancel() {
    this.editModal.close()
  }

  reset() {
    this.loadState()
    this.addressInputTarget.value = this.addressValue
    this.radiusSelect.setValue(this.radiusValue)
    this.updateMap(this.mapTarget)
  }

  loadState() {
    if (this.static) return

    this.latitudeValue = this.latitudeTarget.value || undefined
    this.longitudeValue = this.longitudeTarget.value || undefined
    this.addressValue = this.addressTarget.value || undefined
    this.radiusValue = this.radiusTarget.value
  }

  applyChanges() {
    this.latitudeTarget.value = this.latitudeValue
    this.longitudeTarget.value = this.longitudeValue
    this.addressTarget.value = this.addressValue
    this.radiusTarget.value = this.radiusValue

    this.updateMap(this.thumbnailTarget)
  }

  async search(query) {
    const response = await get(this.searchUrlValue, {
      query: { q: query },
      responseKind: "json"
    })
    const data = await response.json
    return data
  }

  updateMap(element) {
    if (this.hasLatitudeValue && this.hasLongitudeValue) {
      element.dataset.mapCenterValue = JSON.stringify([this.latitudeValue, this.longitudeValue])
    } else {
      delete element.dataset.mapCenterValue
    }
    element.dataset.mapRadiusValue = this.radiusValue
  }

  get radiusSelect() {
    return this.radiusSelectTarget["select"]
  }

  get editModal() {
    return controller(this.editTarget, "modal")
  }

  get enabled() {
    return this.enableTarget.checked
  }

  get static() {
    return !this.hasLatitudeTarget
  }
}
